import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

import { CellRenderer, CellRenderProps, CellValue } from '../types';

import styles from './markdown.module.scss';

// Heading, pipe-table separator, or fenced code block — the cheap, reliable markers.
const MARKDOWN_MARKERS = [/^#{1,6}\s/m, /^\s*\|.*\|.*\n\s*\|?\s*-{3,}/m, /```/];

export const looksLikeMarkdown = (value: string): boolean => MARKDOWN_MARKERS.some(re => re.test(value));

const detect = (samples: CellValue[]): boolean => {
  const strings = samples.filter((s): s is string => typeof s === 'string' && s !== '');
  if (strings.length === 0) return false;
  return strings.every(looksLikeMarkdown);
};

// react-markdown escapes HTML by default (no rehype-raw), so raw <script>/<img> in a
// cell renders as literal text — no injection path, matching the escape-verbatim rule.
// Reused by the structured renderer for markdown string leaves (e.g. an array of reports).
export const MarkdownBlock = ({ value }: CellRenderProps) => (
  <div className={styles.md}>
    <ReactMarkdown remarkPlugins={[remarkGfm]}>{typeof value === 'string' ? value : String(value)}</ReactMarkdown>
  </div>
);

const Compact = ({ value }: CellRenderProps) => {
  const text = typeof value === 'string' ? value : String(value);
  const firstLine =
    text
      .split('\n')
      .find(l => l.trim().length > 0)
      ?.replace(/^#+\s*/, '') ?? '';
  return (
    <div className={styles.compactWrap}>
      <span className={styles.kind}>markdown</span>
      <span className={styles.peek}>{firstLine}</span>
    </div>
  );
};

export const markdownRenderer: CellRenderer = {
  kind: 'markdown',
  detect,
  Compact,
  Expanded: MarkdownBlock,
  Full: MarkdownBlock,
};
